import Vue from 'vue';
import { Module } from 'vuex';
import { RootState } from '@/types/store';
import { Toast } from 'vant';
import { objectToFormData } from 'object-to-formdata';
import {
    dateFormat,
    dateNow,
    dateOffset,
    getWxLocation,
    is,
    isUndef,
} from '@/lib/utils';
import { get, setex } from '@/lib/service/storage';
import { STORAGE_KEY } from '@/lib/constant';

export interface UtilState {
    /** 最近一次同步服务器的时间戳 */
    timestamp: number;
    /** 请求计数 */
    requesting: number;
    /** 用户位置 */
    location: {
        id: number,
        name: string,
    };
    adList: Activity.Ad[] | null;
    /** 咨询二维码 */
    consultQrcode: string | null;
    /** 是否使用safeArea */
    useSafeArea: boolean;
    /** 通用弹窗 */
    popup: {
        /** 图片链接 */
        src: string | null,
        /** 标题 */
        title: string | null,
        /** 顶部提示文本 */
        desc: string | null,
        /** 底部提示文本 */
        desc2: string | null,
        /** 不再显示 */
        neverDisplay: boolean,
        /** 关闭回调 */
        closeCb: any,
        /** 按钮文本 */
        actionText: any,
        /** 点击按钮时的操作 */
        actionCb: any,
        /** 二维码弹窗是否可见 */
        visible: boolean,
    };
    /** 地址选择 */
    district: {
        /** 最大级别 */
        maxLevel: number;
        /** 是否模态 */
        modal: boolean;
        /** 是否可见 */
        visible?: boolean;
        /** 二级层级（直辖市可以选到3级） */
        level2?: boolean;
        /** 回调逻辑 */
        update: any,
        finish: any,
    };
    upDataObj: {
    };
    /** 余额换购商品 */
    walletGoods: User.WalletGood[] | null;
    /** jssdk过期时间 */
    jssdkParamsExpireAt: number | null;
    wechatPopupVisible: boolean;
}

const m: Module<UtilState, RootState> = {
    state: () => ({
        timestamp: 0,
        requesting: 0,
        location: {
            id: 0,
            name: '',
        },
        adList: null,
        consultQrcode: null,
        useSafeArea: false,
        popup: {
            src: null,
            title: null,
            desc: null,
            desc2: null,
            neverDisplay: false,
            closeCb: null,
            actionText: null,
            actionCb: null,
            visible: false,
        },
        district: {
            maxLevel: 3,
            modal: false,
            visible: false,
            update: null,
            finish: null,
        },
        walletGoods: null,
        jssdkParamsExpireAt: null,
        upDataObj: {},
        wechatPopupVisible: false,
    }),
    mutations: {
      setupdata(state , data ) {  // 上传头像返回的
          state.upDataObj = data;
      },

        /**
         * 开始请求
         */
        startRequest(state) {
            state.requesting += 1;
        },
        /**
         * 结束一次请求
         */
        endRequest(state) {
            const count = state.requesting;
            state.requesting = Math.max(0, count - 1);
        },
        /**
         * 设置服务器时间戳
         */
        setServerTimestamp(state, timestamp: number) {
            state.timestamp = timestamp;
        },

        /**
         * 设置地址
         */
        setLocation(state, { location, forceUpdate }) {
            state.location = location;
            if (location && forceUpdate) {
                setex(STORAGE_KEY.GET_CURRENT_LOCATION, JSON.stringify(location), dateFormat(dateOffset(dateNow(), 1)));
            }
        },
        /**
         * 记录ad
         */
        setUserCenterAd(state, adList) {
            state.adList = adList;
        },
        /**
         * 记录咨询二维码
         */
        setConsultQrCode(state, qrcode) {
            state.consultQrcode = qrcode;
        },
        setHistoryLength(state, length) {
            state.useSafeArea = (length === 1 && is.iPhoneXSeries());
        },
        /**
         * 记录二维码是否可见
         */
        setPopupVisible(state, visible) {
            state.popup.visible = visible;
        },
        /**
         * 设置二维码内容
         *
         * @param {} src
         * @param {} title
         * @param {} desc
         */
        setPopupContent(state, { src, title, desc, desc2, actionText, actionCb, neverDisplay, cb }) {
            const popup = state.popup;
            popup.src = src;
            popup.title = title;
            popup.desc = desc;
            popup.desc2 = desc2;
            popup.neverDisplay = neverDisplay;
            popup.closeCb = cb;
            popup.actionText = actionText;
            popup.actionCb = actionCb;
        },
        /**
         * 记录address的popup
         */
        setDistrictPopup(state, { maxLevel, visible, modal, update, finish, level2 }) {
            const district = state.district;
            district.maxLevel = maxLevel || 3;
            district.visible = visible;
            district.level2 = level2;
            district.modal = modal || false;
            district.update = update;
            district.finish = finish;
        },
        /**
         * 关闭地址弹窗
         */
        closeDistrictPopup(state) {
            state.district.maxLevel = 3;
            state.district.visible = false;
            state.district.modal = false;
            state.district.update = null;
            state.district.finish = null;
        },
        /**
         * 设置余额换购商品
         */
        setWalletGoods(state, goods) {
            state.walletGoods = goods;
        },
        /**
         * 记录jssdk参数的过期时间
         */
        setJSSDKParamsExpireAt(state, expireAt) {
            state.jssdkParamsExpireAt = expireAt;
        },
        setWechatPopupVisible(state, visible) {
            state.wechatPopupVisible = visible;
        },
    },
    actions: {
        /**
         * 图片上传
         *
         * @param {String} mediaId
         * @param {String} type
         * @param file 如果使用file需要使用FormData方式发送数据
         *
         * @return {Object} rdata
         *  - status 0表示成功，-1表示失败
         *  - message 失败信息
         */
        async uploadMedia({commit}, { mediaId, file, type, square }) {
            let data: any = null;
            if (isUndef(file)) {
                data = {
                    media_id: mediaId,
                    type,
                    square,
                };
            } else {
                data = objectToFormData({
                    file,
                    type,
                    square,
                });
            }

            const response = await Vue.axios.post('/api/v1/framework/upload_image', data);
            const rdata = response.data;
            if (rdata.status !== 0) {
                Toast(rdata.message || '上传错误');
            }
            commit('setupdata', rdata.data);
            return rdata;
        },
        /**
         * 获得jssdk参数
         *
         * @param {} url 链接
         * @param {} forceUpdate 是否强制更新
         */
        getJSSDKParams({ state, rootState, commit }, { url, forceUpdate }) {
            if (!rootState.jssdk._cacheJSSDKParams || forceUpdate || !state.jssdkParamsExpireAt || (state.jssdkParamsExpireAt < Date.now())) {
                return Vue.axios.get(`/api/v1/framework/js_sdk_config?url=${url}`).then((response) => {
                    const rdata = response.data;
                    if (rdata.status === 0) {
                        const jssdk = rdata.data;
                        commit('setJSSDKParams', jssdk);
                        commit('cacheJSSDKParams', true);
                        // 设置过期时间为30分钟
                        commit('setJSSDKParamsExpireAt', Date.now() + 30 * 60 * 1000);
                        return jssdk;
                    }
                });
            } else {
                return rootState.jssdk.jssdkParams;
            }
        },
        /**
         * 获取定位
         */
        async getLocation(context, forceUpdate = false): Promise<{ id: number, name: string } | boolean> {
            if (!forceUpdate) {
                const currentLocation = get(STORAGE_KEY.GET_CURRENT_LOCATION);
                if (currentLocation) {
                    context.commit('setLocation', {
                        location: JSON.parse(currentLocation),
                        forceUpdate: false,
                    });
                    return true;
                }
            }
            const location = await getWxLocation();
            if (!location) {
                return false;
            }
            if (location.status === 'success' && location.res) {
                const response = await Vue.axios.get('/api/v1/framework/decode_geo_location', {
                    params: {
                        latitude: location.res.latitude,
                        longitude: location.res.longitude,
                    },
                });
                const rdata = response.data;
                if (rdata.status === 0) {
                    if (rdata.data) {
                        context.commit('setLocation', {
                            location: rdata.data,
                            forceUpdate: true,
                        });
                    }
                } else {
                    Toast(rdata.message);
                }
                return rdata;
            } else {
                if (forceUpdate) {
                    // 社区那边要这样提示…
                    Toast('请在手机设置中允许微信使用GPS定位');
                }
                return false;
            }
        },
        /**
         * 获取个人中心广告
         */
        async getUserCenterAd({ state, commit }) {
            const response = await Vue.axios.get('/api/v1/framework/user_center_ads');
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setUserCenterAd', rdata.data);
            }
            return rdata;
        },
        /**
         * 获得咨询二维码
         */
        async getConsultQrCode({ state, commit }) {
            if (state.consultQrcode) {
                return;
            }
            const response = await Vue.axios.get('/api/v1/user/staff_qr_code');
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setConsultQrCode', rdata.data.qr_code);
            }
            return rdata;
        },
        /**
         * 获取商城登录信息
         */
        async getStoreJwt({ state, commit }) {
            const response = await Vue.axios.get('/api/v1/store/login');
            const rdata = response.data;
            return rdata;
        },
        /**
         * 获取换购商品信息
         */
        async getWalletGoods({ state, commit }, { forceUpdate } = {}) {
            if (state.walletGoods && !forceUpdate) {
                return;
            }
            const response = await Vue.axios.get('/api/v1/framework/wallet_goods');
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setWalletGoods', rdata.data);
            }
            return rdata;
        },
    },
};

export default m;
